import { z } from "zod";
import { Contacts, ContactsSchema } from "./commons";
import {
  BriefingLanguages,
  Countries,
  countryEnumToNumber,
  countryNumToEnum,
  Languages,
} from "./enums";
import { apiListSchema } from "./list";

//? DTO
export const CreateAgencyDtoSchema = z.object({
  Year: z.number(),
  Name: z.string(),
  Contacts: z.string().nullish(),
  Website: z.string().nullish(),
  Notes: z.string().nullish(),
  Country: z.number().nullish(),
  Tags: z.string().nullish(),
  Language: z.string().nullish(),
  PriceCommission: z.number().nullish(),
  SeasonSupplementCommission: z.number().nullish(),
  IndividualRoomSupplementCommission: z.number().nullish(),
  SoloTravelerCommission: z.number().nullish(),
  BikesCommissions: z.number().nullish(),
  ExtraNightsCommissions: z.number().nullish(),
  RoomChangeCommissions: z.number().nullish(),
  ServicesCommissions: z.number().nullish(),
  PaymentConditions: z.number().nullish(),
  TimeRestraintForDocumentation: z.number().nullish(),
  Branding: z.boolean().nullish(),
  Payment: z.number().nullish(),
  Favourite: z.boolean().nullish(),
  Details: z.string().nullish(),
  DigitalBook: z.string().nullish(),
  NavigationApp: z.string().nullish(),
  Tours: z.string().nullish(),
  Guiding: z.string().nullish(),
  BriefingLanguage: z.string().nullish(),
  TravefyRequired: z.boolean().nullish(),
  Rwgpsrequired: z.boolean().nullish(),
});

export type CreateAgencyDto = z.infer<typeof CreateAgencyDtoSchema>;

export const AgencyDtoSchema = CreateAgencyDtoSchema.extend({
  Id: z.number(),
});
export type AgencyDto = z.infer<typeof AgencyDtoSchema>;

export const agencyDtoListSchema = apiListSchema(AgencyDtoSchema);

//? Model

export enum PaymentConditions {
  "Monthly" = "Monthly",
  "BeforeTour" = "BeforeTour",
  "AfterTour" = "AfterTour",
}
export const PaymentConditionsTypesAsArray = Object.values(PaymentConditions);

export const paymentConditionsEnumToNumber: Record<PaymentConditions, number> =
  {
    AfterTour: 0,
    BeforeTour: 1,
    Monthly: 2,
  };

export const paymentConditionsNumToEnum = Object.entries(
  paymentConditionsEnumToNumber
).reduce((acc, [key, value]) => ({ ...acc, [value]: key }), {}) as Record<
  number,
  PaymentConditions
>;

export enum PaymentCompany {
  "tourManagement" = "tourManagement",
  "Other" = "Other",
}
export const PaymentCompanyTypesAsArray = Object.values(PaymentCompany);

export const paymentCompanyEnumToNumber: Record<PaymentCompany, number> = {
  Other: 0,
  tourManagement: 1,
};

export const paymentCompanyNumToEnum = Object.entries(
  paymentCompanyEnumToNumber
).reduce((acc, [key, value]) => ({ ...acc, [value]: key }), {}) as Record<
  number,
  PaymentCompany
>;

export enum DigitalBook {
  "tourManagement" = "tourManagement",
  "AgencyBranding" = "AgencyBranding",
  "AgencyApp" = "AgencyApp",
  "NotNecessary" = "NotNecessary",
}
export const DigitalBookTypesAsArray = Object.values(DigitalBook);

export enum AgencyTour {
  tourManagementTours = "tourManagementTours",
  Exclusive = "Exclusive",
}
export const AgencyTourTypesAsArray = Object.values(AgencyTour);

export enum Guiding {
  Guided = "Guided",
  SelfGuided = "SelfGuided",
}
export const GuidingTypesAsArray = Object.values(Guiding);

export const NavigationApp = [
  "tour-management",
  "AgencyApp",
  "NotNecessary",
] as const;

export const CreateAgencySchema = z.object({
  year: z.number().min(1),
  name: z.string().min(1),

  contacts: ContactsSchema,
  website: z.string().nullish(),
  notes: z.string().nullish(),
  country: z
    .string()
    .refine((v) => {
      if (!v) return true;
      return Countries.includes(v);
    })
    .nullish(),
  tags: z.array(z.string()).nullish(),
  language: z
    .string()
    .nullish()
    .refine((val) => {
      if (!val) return true;
      return Languages.includes(val as (typeof Languages)[number]);
    }),

  priceCommission: z.number().min(0).nullish(),
  seasonSupplementCommission: z.number().min(0).nullish(),
  individualRoomSupplementCommission: z.number().min(0).nullish(),
  soloTravelerCommission: z.number().min(0).nullish(),
  bikesCommissions: z.number().min(0).nullish(),
  extraNightsCommissions: z.number().min(0).nullish(),
  roomChangeCommissions: z.number().min(0).nullish(),
  servicesCommissions: z.number().min(0).nullish(),

  paymentConditions: z.nativeEnum(PaymentConditions).nullish(),
  timeRestraintForDocumentation: z.number().nullish(),
  branding: z.boolean().nullish(),

  payment: z.nativeEnum(PaymentCompany).nullish(),
  favorite: z.boolean().nullish(),

  details: z.string().nullish(),
  digitalBook: z.nativeEnum(DigitalBook).nullish(),
  navigationApp: z
    .string()
    .nullish()
    .refine((val) => {
      if (!val) return true;
      return NavigationApp.includes(val as (typeof NavigationApp)[number]);
    }),

  tours: z.array(z.nativeEnum(AgencyTour)).nullish(),
  guiding: z.array(z.nativeEnum(Guiding)).nullish(),
  briefingLanguage: z
    .array(z.enum(BriefingLanguages))
    .nullish()
    .refine((val) => {
      if (!val) return true;
      return val.every((v) =>
        BriefingLanguages.includes(v as (typeof BriefingLanguages)[number])
      );
    }),
  travefyRequired: z.boolean().nullish(),
  rwgpsrequired: z.boolean().nullish(),
});
export type CreateAgency = z.infer<typeof CreateAgencySchema>;

export const AgencySchema = CreateAgencySchema.extend({
  id: z.string().min(1),
});

export type Agency = z.infer<typeof AgencySchema>;

//? Mappers

export function agencyFromDto(dto: AgencyDto): Agency {
  const parsedContacts = dto.Contacts
    ? ContactsSchema.safeParse(JSON.parse(dto.Contacts))
    : undefined;
  let contacts: Contacts = [];
  if (parsedContacts?.success) {
    contacts = parsedContacts.data;
  }
  return {
    id: dto.Id.toString(),
    branding: dto.Branding,
    favorite: dto.Favourite,
    contacts,
    language: dto.Language,
    name: dto.Name,
    notes: dto.Notes ?? "",
    payment:
      typeof dto.Payment === "number"
        ? paymentCompanyNumToEnum[dto.Payment]
        : undefined,
    paymentConditions:
      typeof dto.PaymentConditions === "number"
        ? paymentConditionsNumToEnum[dto.PaymentConditions]
        : undefined,
    website: dto.Website,
    year: dto.Year,
    country: dto.Country ? countryNumToEnum[dto.Country.toString()] : undefined,
    bikesCommissions: dto.BikesCommissions,
    details: dto.Details,
    digitalBook: dto.DigitalBook as DigitalBook,
    extraNightsCommissions: dto.ExtraNightsCommissions,
    individualRoomSupplementCommission: dto.IndividualRoomSupplementCommission,
    navigationApp: dto.NavigationApp,
    priceCommission: dto.PriceCommission,
    roomChangeCommissions: dto.RoomChangeCommissions,
    seasonSupplementCommission: dto.SeasonSupplementCommission,
    servicesCommissions: dto.ServicesCommissions,
    soloTravelerCommission: dto.SoloTravelerCommission,
    tags: dto.Tags?.split("|").filter(Boolean) ?? [],
    timeRestraintForDocumentation: dto.TimeRestraintForDocumentation,
    tours: dto.Tours?.split("|")
      ?.filter(Boolean)
      .map((v) => v as AgencyTour),
    guiding: dto.Guiding?.split("|")
      ?.filter(Boolean)
      .map((v) => v as Guiding),
    briefingLanguage: dto.BriefingLanguage?.split("|").map(
      (v) => v as (typeof BriefingLanguages)[number]
    ),
    travefyRequired: dto.TravefyRequired,
    rwgpsrequired: dto.Rwgpsrequired,
  };
}
export function agencyToDto(model: Agency): AgencyDto {
  return {
    ...createAgencyToDto(model),
    Id: Number.parseInt(model.id),
  };
}

export function createAgencyToDto(model: CreateAgency): CreateAgencyDto {
  return {
    BikesCommissions: model.bikesCommissions,
    Branding: model.branding,
    Contacts: model.contacts ? JSON.stringify(model.contacts) : null,
    Country: model.country ? countryEnumToNumber[model.country] : undefined,
    Details: model.details ?? "",
    DigitalBook: model.digitalBook ?? "",
    ExtraNightsCommissions: model.extraNightsCommissions,
    Favourite: model.favorite,
    IndividualRoomSupplementCommission:
      model.individualRoomSupplementCommission,
    Language: model.language,
    Name: model.name,
    NavigationApp: model.navigationApp,
    Notes: model.notes ?? "",
    Payment: model.payment
      ? paymentCompanyEnumToNumber[model.payment]
      : undefined,
    PaymentConditions: model.paymentConditions
      ? paymentConditionsEnumToNumber[model.paymentConditions]
      : undefined,
    PriceCommission: model.priceCommission,
    RoomChangeCommissions: model.roomChangeCommissions,
    SeasonSupplementCommission: model.seasonSupplementCommission,
    ServicesCommissions: model.servicesCommissions,
    SoloTravelerCommission: model.soloTravelerCommission,
    Tags: model.tags?.join("|") ?? "",
    TimeRestraintForDocumentation: model.timeRestraintForDocumentation,
    Website: model.website,
    Year: model.year,
    Tours: model.tours?.join("|"),
    Guiding: model.guiding?.join("|"),
    BriefingLanguage: model.briefingLanguage?.join("|"),
    TravefyRequired: model.travefyRequired,
    Rwgpsrequired: model.rwgpsrequired,
  };
}
